import type { CityConfig } from "../city-template/types";

export const santaBarbaraConfig: CityConfig = {
  city: "Santa Barbara",
  state: "California",
  stateAbbr: "CA",
  slug: "santa-barbara",
  route: "/cities/santa-barbara",
  title: "Emergency Passport Santa Barbara, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Santa Barbara, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for the American Riviera and Central Coast travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Santa Barbara, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Santa Barbara and Central Coast travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Santa%20Barbara%20California%20coastal%20cityscape%20at%20golden%20hour%20with%20red%20tile%20rooftops%2C%20Spanish%20colonial%20architecture%2C%20Stearns%20Wharf%20pier%20extending%20into%20the%20Pacific%20Ocean%2C%20palm%20trees%20along%20the%20shoreline%2C%20Santa%20Ynez%20Mountains%20in%20the%20background%2C%20warm%20amber%20sunset%20light%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20beautiful%20American%20Riviera%20coastal%20environment&width=1800&height=700&seq=santa-barbara-hero-001&orientation=landscape",
  heroImageAlt: "Santa Barbara California skyline at golden hour with red tile roofs and Pacific Ocean",
  geoPosition: "34.4208;-119.6982",
  geoPlacename: "Santa Barbara, California",
  lastModified: "2026-07-21",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd Suite 1000, Los Angeles, CA 90024",
    distance: "Located in West LA — approximately 95–110 minutes from Santa Barbara via US-101",
  },
  nearbyCitiesZips: [
    { name: "Santa Barbara", zips: "93101–93111, 93116–93121, 93130, 93140, 93150, 93160, 93190, 93199" },
    { name: "Goleta", zips: "93116, 93117, 93118, 93199" },
    { name: "Isla Vista", zips: "93117" },
    { name: "Carpinteria", zips: "93013, 93014" },
    { name: "Montecito", zips: "93108" },
    { name: "Summerland", zips: "93067" },
    { name: "Hope Ranch", zips: "93110" },
    { name: "Ventura", zips: "93001–93009" },
    { name: "Oxnard", zips: "93030–93036" },
    { name: "Camarillo", zips: "93010, 93011, 93012" },
    { name: "Ojai", zips: "93023, 93024" },
    { name: "San Luis Obispo", zips: "93401–93410" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Central Coast departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Sacramento", route: "/cities/sacramento" },
    { name: "Irvine", route: "/cities/irvine" },
    { name: "Santa Ana", route: "/cities/santa-ana" },
    { name: "Fresno", route: "/cities/fresno" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Santa Barbara remotely via secure courier. We do not have a walk-in office in Santa Barbara — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Central Coast travelers.",
  fpcOfficeAddress: "355 S Grand Ave, Ste 2450, Los Angeles, CA 90071",
  fpcOfficeDistance: "Serving all of Santa Barbara County",
  serviceAreaIntro: "From downtown State Street to the Mesa, from Montecito to Goleta — Fast Passport Center serves the entire Santa Barbara community. We regularly help travelers from Carpinteria, Ventura, and San Luis Obispo get their passports fast.",
  nearbyCitiesIntro: "We serve all Santa Barbara neighborhoods and nearby Central Coast cities including Goleta, Carpinteria, Montecito, Ventura, Oxnard, and Camarillo. If you are on the California coast between LA and San Francisco, we have you covered.",
  howItWorksIntro: "Most Santa Barbara travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Santa Barbara Airport (SBA) serving as a popular regional gateway for Central Coast travelers, Santa Barbara residents can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Santa Barbara and Central Coast travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Santa Barbara? Here are the most common ones we hear from Central Coast travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Central Coast international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Thomas B.",
      location: "Santa Barbara, CA",
      rating: 5,
      text: "My trip to Paris was in 3 days and my passport had expired. Fast Passport Center got me a new one in under 48 hours. Saved our anniversary trip completely.",
      service: "Emergency Passport",
    },
    {
      name: "Natalie S.",
      location: "Goleta, CA",
      rating: 5,
      text: "Needed to renew for a business trip to London. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for Central Coast residents.",
      service: "Passport Renewal",
    },
    {
      name: "Andrew C.",
      location: "Montecito, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Emily D.",
      location: "Carpinteria, CA",
      rating: 5,
      text: "As a frequent international traveler from the Santa Barbara area, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "sb-1",
      name: "Santa Barbara Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "836 Anacapa St",
      city: "Santa Barbara",
      state: "CA",
      zip: "93101",
      phone: "(805) 963-3744",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Santa Barbara",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-2",
      name: "Goleta Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "555 N Fairview Ave",
      city: "Goleta",
      state: "CA",
      zip: "93117",
      phone: "(805) 967-3762",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from UC Santa Barbara",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Goleta and Isla Vista residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-3",
      name: "Carpinteria Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1076 Casitas Pass Rd",
      city: "Carpinteria",
      state: "CA",
      zip: "93013",
      phone: "(805) 684-4413",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Carpinteria",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Santa Barbara County residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-4",
      name: "Ventura Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "675 E Santa Clara St",
      city: "Ventura",
      state: "CA",
      zip: "93001",
      phone: "(805) 648-7731",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Ventura",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Ventura County. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-5",
      name: "Oxnard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 E 4th St",
      city: "Oxnard",
      state: "CA",
      zip: "93030",
      phone: "(805) 483-2711",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Oxnard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern Ventura County residents. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "sb-6",
      name: "Santa Barbara City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "735 Anacapa St",
      city: "Santa Barbara",
      state: "CA",
      zip: "93101",
      phone: "(805) 564-5309",
      hours: "Mon–Thu: 7:30 AM – 5:30 PM, Fri: 8:00 AM – 5:00 PM",
      distance: "0.2 miles from Santa Barbara City Hall",
      website: "https://www.santabarbaraca.gov/",
      websiteLabel: "Santa Barbara City Clerk",
      notes: "Major city facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Central Coast Coverage", agency: "No Central Coast expertise", fpc: "American Riviera, UCSB, and Central Coast coastal travel expertise" },
    { feature: "Multi-Person Processing", agency: "Separate appointments per person", fpc: "Handle families and groups in parallel" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Coastal California photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Santa Barbara County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Santa Barbara applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Central Coast families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Santa Barbara travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Santa Barbara." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Santa Barbara address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Santa Barbara before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Santa Barbara for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Central Coast travelers." },
    { q: "How fast can I renew my passport in Santa Barbara?", a: "Fast Passport Center offers fast passport renewal in Santa Barbara in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Santa Barbara in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Santa Barbara?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Santa Barbara residents." },
    { q: "Can I expedite a child passport in Santa Barbara?", a: "Yes. Fast Passport Center expedites child passports in Santa Barbara for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Santa Barbara with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Santa Barbara?",
      a: "With Fast Passport Center's emergency courier service, Santa Barbara travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Santa Barbara?",
      a: "Popular options include the Santa Barbara Main Post Office (93101), Goleta Post Office (93117), Carpinteria Post Office (93013), and Santa Barbara City Clerk (93101). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Santa Barbara?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Goleta, Carpinteria, or Ventura?",
      a: "We serve all of Santa Barbara County and nearby Ventura County including Goleta, Carpinteria, Montecito, Ventura, Oxnard, and Camarillo. Our overnight shipping labels work from any UPS location, so your location along the coast doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Santa Barbara?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Santa Barbara?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Santa Barbara and Goleta offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Santa Barbara?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Santa Barbara?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Santa Barbara County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Santa Barbara", address: "3315 State St, Santa Barbara, CA 93105", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Goleta", address: "5950 Hollister Ave, Goleta, CA 93117", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Santa Barbara", address: "101 S Hope Ave, Santa Barbara, CA 93105", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Ventura", address: "2751 E Main St, Ventura, CA 93003", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Oxnard", address: "2001 N Rose Ave, Oxnard, CA 93036", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Santa Barbara", address: "1920 Cliff Dr, Santa Barbara, CA 93109", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Carpinteria", address: "1000 Casitas Pass Rd, Carpinteria, CA 93013", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Santa Maria", address: "2021 S Bradley Rd, Santa Maria, CA 93454", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Santa Barbara",
      address: "535 S Milpas St, Santa Barbara, CA 93103",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Goleta",
      address: "7127 Hollister Ave, Goleta, CA 93117",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Ventura",
      address: "2001 E Main St, Ventura, CA 93001",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Oxnard",
      address: "1500 E Vineyard Ave, Oxnard, CA 93036",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Ventura County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SBA",
      name: "Santa Barbara Airport",
      distance: "8 miles from downtown Santa Barbara",
      address: "500 James Fowler Rd, Santa Barbara, CA 93117",
      note: "The primary airport for the Central Coast. Nonstop flights to Denver, Dallas, Phoenix, Portland, San Francisco, and seasonal international connections.",
      flightUrl: "https://www.flysba.com/",
      flightLabel: "Santa Barbara Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "95 miles from Santa Barbara",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via US-101.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "85 miles from Santa Barbara",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "Convenient for domestic flights and some international connections. Alternative to LAX for Central Coast travelers.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the California Department of Public Health Vital Records. Online, mail, or in-person options available for Santa Barbara County residents.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through California CDPH Vital Records. Required for estate and legal matters on the Central Coast.", url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Santa Barbara County Clerk-Recorder. Certified copies available for name changes and legal purposes.", url: "https://www.countyofsb.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Santa Barbara County Superior Court. Required for name change passport corrections and legal documentation.", url: "https://www.sbcourts.org/" },
  ],
};